import { Outlet, createRootRoute, createRoute, createRouter, lazyRouteComponent } from '@tanstack/react-router'
import { SiteFooter, SiteHeader } from './components/site-header'
import { SweepBar } from './components/sweep-bar'
import { getBootstrap } from './lib/api'
import type { PublicBootstrap } from './lib/types'
import { NotFoundPage } from './pages/not-found-page'

const loadPublicPages = () => import('./pages/public-pages')
const homePage = lazyRouteComponent(loadPublicPages, 'HomePage')
const jobsPage = lazyRouteComponent(loadPublicPages, 'JobsPage')
const jobOrLandingPage = lazyRouteComponent(loadPublicPages, 'JobOrLandingPage')
const newsletterPage = lazyRouteComponent(loadPublicPages, 'NewsletterPage')
const newsletterPreferencesPage = lazyRouteComponent(loadPublicPages, 'NewsletterPreferencesPage')
const newsletterIssuePage = lazyRouteComponent(loadPublicPages, 'NewsletterIssuePage')
const postJobPage = lazyRouteComponent(loadPublicPages, 'PostJobPage')
const aboutPage = lazyRouteComponent(loadPublicPages, 'AboutPage')
const privacyPage = lazyRouteComponent(loadPublicPages, 'PrivacyPage')
const termsPage = lazyRouteComponent(loadPublicPages, 'TermsPage')
const opsPage = lazyRouteComponent(() => import('./pages/ops-pages'), 'OpsRoute')

function RootLayout() {
  const ops = window.location.pathname.startsWith('/ops')
  if (ops) return <><a className="skip-link" href="#main-content">Skip to operations content</a><Outlet /></>
  const data = getBootstrap() as PublicBootstrap
  return (
    <>
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <SiteHeader />
      <Outlet />
      <SiteFooter />
      <SweepBar reading={data.stats} now={Date.parse(data.serverTime)} />
    </>
  )
}

const rootRoute = createRootRoute({ component: RootLayout, notFoundComponent: NotFoundPage })
const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: homePage })
const jobsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/jobs', component: jobsPage })
const jobRoute = createRoute({ getParentRoute: () => rootRoute, path: '/jobs/$slug', component: jobOrLandingPage })
// `/jobs/<domain>/<language>` and `/jobs/<domain>/remote`. The registry decides
// whether a two-segment path is a real landing; the router only has to stop
// treating it as an unknown route.
const jobCombinationRoute = createRoute({ getParentRoute: () => rootRoute, path: '/jobs/$slug/$modifier', component: jobOrLandingPage })
const newsletterRoute = createRoute({ getParentRoute: () => rootRoute, path: '/newsletter', component: newsletterPage })
const newsletterPreferencesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/newsletter/preferences', component: newsletterPreferencesPage })
const issueRoute = createRoute({ getParentRoute: () => rootRoute, path: '/newsletter/$slug', component: newsletterIssuePage })
const postRoute = createRoute({ getParentRoute: () => rootRoute, path: '/post-a-job', component: postJobPage })
const aboutRoute = createRoute({ getParentRoute: () => rootRoute, path: '/about', component: aboutPage })
const privacyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/privacy', component: privacyPage })
const termsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/terms', component: termsPage })
const opsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/ops', component: opsPage })
const opsSubRoute = createRoute({ getParentRoute: () => rootRoute, path: '/ops/$', component: opsPage })

const routeTree = rootRoute.addChildren([
  indexRoute,
  jobsRoute,
  jobRoute,
  jobCombinationRoute,
  newsletterRoute,
  newsletterPreferencesRoute,
  issueRoute,
  postRoute,
  aboutRoute,
  privacyRoute,
  termsRoute,
  opsRoute,
  opsSubRoute,
])

export const router = createRouter({ routeTree, defaultPreload: 'intent' })

declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}
